/**
 * Die gelieferten Texturen aus `assets/texturen/`.
 *
 * Maßgeblich ist der Kanalvertrag in `assets/texturen/README.md`. Er entscheidet drei
 * Dinge, und wer eines davon falsch macht, bekommt kein Fehlerbild, sondern ein leicht
 * falsches: Normalmaps und Masken sind Daten und dürfen nicht als sRGB gelesen werden,
 * sonst zeigen die Normalen in die falsche Richtung. Die Farbbilder sind sRGB. Und die
 * Rauheit steckt im Alphakanal, nicht im Grünkanal, den three sonst dafür liest.
 *
 * `flipY` steht für jede Datei auf false. Der Generator schreibt die Zeilen von oben nach
 * unten und richtet das Vorzeichen der senkrechten Ableitung danach; three würde die
 * Bilder beim Laden umdrehen, und dann zeigte Grün nach unten statt nach oben, und die
 * Rostspur läge nicht mehr auf der Schweißnaht, zu der sie gehört.
 */
import { NoColorSpace, RepeatWrapping, SRGBColorSpace, Texture, TextureLoader } from 'three'

export type TexturName =
  | 'wasser-normal'
  | 'wasser-gischt'
  | 'sternenhimmel'
  | 'mond'
  | 'stahl'
  | 'stahl-normal'
  | 'deck'
  | 'segeltuch'

export interface TexturVertrag {
  /** 'farbe' wird als sRGB gelesen, 'daten' linear. Normalmaps und Masken sind Daten. */
  art: 'farbe' | 'daten'
  /** Kachelnde Muster wiederholen, einmalige Bilder (Mond, Himmel) nicht. */
  kachelt: boolean
  /** Kantenlänge der Kachel in Metern, soweit die Lieferung eine nennt. */
  kachelM?: number
  /**
   * Wie schräg die Fläche gelesen wird. Das Wasser füllt den halben Bildschirm und
   * liegt flach im Blick, dort kostet jede Stufe echte Rechenzeit; der Himmel steht
   * quer zum Blick und braucht keine. Auf den Rümpfen zahlt sie sich aus, weil die
   * Bordwand schräg steht und ohne sie flimmert.
   */
  schraeg: 1 | 2 | 4
}

/**
 * Der Vertrag je Datei. Die Tabelle ist die einzige Stelle, an der steht, wie eine
 * gelieferte Textur zu lesen ist; `test/texturen.test.ts` hält sie gegen die Lieferung.
 */
export const VERTRAG: Record<TexturName, TexturVertrag> = {
  'wasser-normal': { art: 'daten', kachelt: true, schraeg: 1 },
  'wasser-gischt': { art: 'daten', kachelt: true, schraeg: 1 },
  sternenhimmel: { art: 'farbe', kachelt: false, schraeg: 1 },
  mond: { art: 'farbe', kachelt: false, schraeg: 1 },
  stahl: { art: 'farbe', kachelt: true, kachelM: 4, schraeg: 4 },
  'stahl-normal': { art: 'daten', kachelt: true, kachelM: 4, schraeg: 4 },
  deck: { art: 'farbe', kachelt: true, kachelM: 4, schraeg: 4 },
  segeltuch: { art: 'farbe', kachelt: true, kachelM: 4, schraeg: 4 },
}

const urls = import.meta.glob('../../assets/texturen/*.png', {
  query: '?url',
  import: 'default',
  eager: true,
}) as Record<string, string>

export function urlFor(name: TexturName): string | undefined {
  const key = Object.keys(urls).find((k) => k.endsWith(`/${name}.png`))
  return key ? urls[key] : undefined
}

const geladen = new Map<TexturName, Texture>()

/**
 * Woher eine Textur kommt. Die Szene nimmt die geladene Lieferung, eine Prüfung nimmt
 * ihre eigenen Bilder: sonst ließe sich die Bindung nur im Browser prüfen.
 */
export type TexturQuelle = (name: TexturName) => Texture | undefined

/** Setzt den Kanalvertrag auf eine geladene Datei. */
export function bereiteVor(texture: Texture, name: TexturName): Texture {
  const vertrag = VERTRAG[name]
  texture.colorSpace = vertrag.art === 'farbe' ? SRGBColorSpace : NoColorSpace
  texture.flipY = false
  if (vertrag.kachelt) {
    texture.wrapS = RepeatWrapping
    texture.wrapT = RepeatWrapping
  }
  texture.anisotropy = vertrag.schraeg
  texture.needsUpdate = true
  return texture
}

/**
 * Lädt alle Texturen einmal. Fehlt eine Datei, bleibt sie weg: die App zeigt dann ihr
 * altes Bild und kein fehlendes Lichterbild, wie schon bei den Rümpfen.
 */
export async function preloadTexturen(): Promise<void> {
  const loader = new TextureLoader()
  await Promise.all(
    (Object.keys(VERTRAG) as TexturName[]).map(async (name) => {
      if (geladen.has(name)) return
      const url = urlFor(name)
      if (!url) return
      try {
        geladen.set(name, bereiteVor(await loader.loadAsync(url), name))
      } catch {
        // Eine kaputte Datei darf die Szene nicht aufhalten.
      }
    }),
  )
}

export function textur(name: TexturName): Texture | undefined {
  return geladen.get(name)
}

/**
 * Schneidet eine Hälfte aus dem Deckatlas.
 *
 * Der Atlas führt links Holz und rechts Blech, jede Hälfte kachelt für sich. Mit
 * `repeat` und `offset` allein ginge das nicht: beim Überlauf liefe das Holz in das
 * Blech, und die Mipmaps blendeten an der Materialgrenze zwischen beiden. Deshalb
 * schneidet die App die Hälfte einmal heraus. Das erzeugt keine neuen Bildpunkte, es
 * nimmt die gelieferten.
 */
/** Der Bildausschnitt einer Atlashälfte. Links liegt Holz, rechts Blech. */
export function atlasAusschnitt(breite: number, haelfte: 'links' | 'rechts'): { x: number; breite: number } {
  const halb = Math.floor(breite / 2)
  return { x: haelfte === 'links' ? 0 : halb, breite: halb }
}

/**
 * Welchen Bildpunkt eine Texturkoordinate trifft, wenn die Textur wiederholt wird.
 *
 * Dieselbe Rechnung wie `RepeatWrapping` auf der Grafikkarte, hier in TypeScript, damit
 * die Prüfung ohne Browser zeigen kann, dass ein Überlauf in derselben Atlashälfte
 * landet und nicht im Nachbarmaterial.
 */
export function kachelIndex(u: number, breite: number): number {
  const anteil = ((u % 1) + 1) % 1
  return Math.min(breite - 1, Math.floor(anteil * breite))
}

export function atlasHaelfte(atlas: Texture, haelfte: 'links' | 'rechts'): Texture | undefined {
  const bild = atlas.image as HTMLImageElement | undefined
  if (!bild || !bild.width) return undefined
  const { x, breite } = atlasAusschnitt(bild.width, haelfte)
  const canvas = document.createElement('canvas')
  canvas.width = breite
  canvas.height = bild.height
  const ctx = canvas.getContext('2d')
  if (!ctx) return undefined
  ctx.drawImage(bild, x, 0, breite, bild.height, 0, 0, breite, bild.height)
  const texture = new Texture(canvas)
  texture.colorSpace = atlas.colorSpace
  texture.flipY = false
  texture.wrapS = RepeatWrapping
  texture.wrapT = RepeatWrapping
  texture.anisotropy = atlas.anisotropy
  texture.needsUpdate = true
  return texture
}
